import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, NetworkCheck, Key as KeyIcon } from '@mui/icons-material';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
} from 'recharts';
import { toast } from 'react-toastify';

import StatCard from '../../components/admin/StatCard';
import {
  getMistralSettings, setMistralKey, testMistral, getMistralUsage, MistralSettings, MistralUsage,
} from '../../services/adminMistral';

const ConfigurationPage: React.FC = () => {
  const [settings, setSettings] = useState<MistralSettings | null>(null);
  const [usage, setUsage] = useState<MistralUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, u] = await Promise.all([getMistralSettings(), getMistralUsage(30)]);
      setSettings(s);
      setUsage(u);
    } catch (e) {
      // interceptor toasts
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (newKey.trim().length < 12) {
      toast.error('API key looks too short');
      return;
    }
    try {
      setSaving(true);
      const s = await setMistralKey(newKey.trim());
      setSettings((prev) => ({ ...(prev as MistralSettings), ...s }));
      setNewKey('');
      toast.success('Mistral API key saved. New calls use it immediately; restart the backend so all services pick it up.');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to save key');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const r = await testMistral();
      if (r.ok) toast.success(`Connection OK (${r.latency_ms} ms)`);
      else toast.error(`Connection failed: ${r.error || 'unknown error'}`);
      load();
    } catch (e: any) {
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading || !settings) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Configuration</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Mistral AI API key and usage.</Typography>

      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={4}><StatCard label="Requests (30d)" value={(usage?.total_requests ?? 0).toLocaleString()} icon={<NetworkCheck />} color="#1976D2" /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Tokens (30d)" value={(usage?.total_tokens ?? 0).toLocaleString()} icon={<KeyIcon />} color="#8E24AA" /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Est. cost (30d)" value={`€${(usage?.est_cost ?? 0).toFixed(2)}`} icon={<NetworkCheck />} color="#F26F21" /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 3, mb: 3, maxWidth: 720 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={0.5}>Mistral API key</Typography>
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <Chip
            size="small"
            label={settings.configured ? `Configured (${settings.masked_key})` : 'Not configured'}
            color={settings.configured ? 'success' : 'default'}
          />
          <Chip size="small" variant="outlined" label={`source: ${settings.source}`} />
          <Chip size="small" variant="outlined" label={`model: ${settings.model}`} />
          {settings.last_tested && (
            <Chip
              size="small"
              color={settings.last_test_ok ? 'success' : 'error'}
              label={`last test: ${settings.last_test_ok ? 'OK' : 'failed'} · ${new Date(settings.last_tested).toLocaleString()}`}
            />
          )}
        </Box>
        <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
          <TextField
            label="New API key"
            type="password"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="Paste a new Mistral API key"
            sx={{ flex: 1, minWidth: 280 }}
            helperText="Stored encrypted at rest. New API calls use it right away; long-running services pick it up after the next backend restart."
          />
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || !newKey} sx={{ mt: 1 }}>
            {saving ? 'Saving...' : 'Save key'}
          </Button>
          <Button variant="outlined" startIcon={<NetworkCheck />} onClick={handleTest} disabled={testing} sx={{ mt: 1 }}>
            {testing ? 'Testing...' : 'Test connection'}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>Mistral usage (30 days)</Typography>
        {usage && usage.series.length > 0 ? (
          <Box sx={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={usage.series}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis yAxisId="left" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" fontSize={12} />
                <ReTooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="requests" name="Requests" stroke="#1976D2" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="tokens" name="Tokens" stroke="#8E24AA" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No Mistral calls recorded yet in this window. Usage appears here as documents are processed.
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Cost is an indicative estimate (token × per-model rate), not a Mistral invoice.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ConfigurationPage;
